import React, { useEffect, useState } from 'react';
import { teamAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import BackButton from '../components/BackButton';
import { COUNTRIES } from '../data/countries';

const PERMISSION_DEFS = [
  { key: 'manage_sales',     label: 'Record sales',           desc: 'Can enter new sales and log payments' },
  { key: 'manage_purchases', label: 'Record purchases',       desc: 'Can restock products and record production' },
  { key: 'manage_products',  label: 'Manage products',        desc: 'Can add, edit and delete products' },
  { key: 'view_customers',   label: 'View customers & debts', desc: 'Can see the customer list, profiles and who owes what' },
  { key: 'manage_expenses',  label: 'Record expenses',        desc: 'Can add, edit and delete expenses' },
  { key: 'view_financials',  label: 'View financials',        desc: 'Can see revenue, profit and the dashboard totals' },
];

function PermissionsPanel({ member, onSaved }) {
  const { toast } = useToast();
  const [perms,   setPerms]   = useState(member.permissions || {});
  const [saving,  setSaving]  = useState(false);
  const [open,    setOpen]    = useState(false);

  const toggle = (key) => setPerms(p => ({ ...p, [key]: !p[key] }));

  const save = async () => {
    setSaving(true);
    try {
      await teamAPI.updatePermissions(member.id, { permissions: perms });
      toast('Permissions saved', 'success');
      onSaved();
    } catch {
      toast('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.6rem' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)',
          fontSize: '0.78rem', fontWeight: 600, padding: 0 }}
      >
        {open ? '▲ Hide permissions' : '▼ Manage permissions'}
      </button>

      {open && (
        <div style={{ marginTop: '0.65rem', display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          {PERMISSION_DEFS.map(({ key, label, desc }) => (
            <label key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer', gap: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.84rem', fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: '0.73rem', color: 'var(--color-muted)' }}>{desc}</div>
              </div>
              <div
                onClick={() => toggle(key)}
                style={{
                  flexShrink: 0,
                  width: '42px', height: '24px',
                  borderRadius: '999px',
                  background: perms[key] ? 'var(--color-primary)' : 'rgba(255,255,255,0.12)',
                  position: 'relative',
                  transition: 'background 0.2s',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  position: 'absolute', top: '3px',
                  left: perms[key] ? '21px' : '3px',
                  width: '18px', height: '18px',
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }} />
              </div>
            </label>
          ))}

          <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', padding: '0.4rem 0.6rem',
            background: 'rgba(255,255,255,0.04)', borderRadius: '6px', marginTop: '0.25rem' }}>
            Always owner/manager only: voiding records, assets, company data, team management, settings and exports.
          </div>

          <button
            className="btn-primary"
            onClick={save}
            disabled={saving}
            style={{ alignSelf: 'flex-start', padding: '0.4rem 1rem', fontSize: '0.82rem' }}
          >
            {saving ? 'Saving…' : 'Save permissions'}
          </button>
        </div>
      )}
    </div>
  );
}

function MemberCard({ m, onDeactivate, onReactivate, onResetPassword, onSaved }) {
  const roleDef = ROLE_DEFS[m.role] || ROLE_DEFS.staff;
  return (
    <div className="card" style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
            {m.name}{' '}
            {m.deactivated_at && <span style={{ color: 'var(--color-danger)', fontSize: '0.75rem' }}>· deactivated</span>}
          </div>
          <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.1rem' }}>
            {m.phone ? `+${m.phone}` : m.email}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '0.7rem', fontWeight: 700, padding: '0.15rem 0.5rem',
              borderRadius: '999px', background: roleDef.color, color: roleDef.textColor,
            }}>
              {roleDef.label}
            </span>
            {m.business_name && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.45rem',
                borderRadius: '999px', background: 'rgba(99,102,241,0.12)', color: 'var(--color-primary)',
              }}>
                {m.business_name}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', flexShrink: 0 }}>
          {!m.deactivated_at ? (
            <>
              <button className="btn-ghost" style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                onClick={() => onResetPassword(m)}>
                Reset password
              </button>
              <button className="btn-danger" style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
                onClick={() => onDeactivate(m.id, m.role)}>
                Remove
              </button>
            </>
          ) : (
            <button className="btn-ghost" style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem' }}
              onClick={() => onReactivate(m.id)}>
              Reactivate
            </button>
          )}
        </div>
      </div>
      {!m.deactivated_at && m.must_change_password && (
        <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.4rem' }}>
          Hasn't set their own password yet (still on the temporary one).
        </div>
      )}

      {/* Managers bypass requirePermission — show info badge instead of toggles */}
      {!m.deactivated_at && m.role === 'manager' && (
        <div style={{
          marginTop: '0.75rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.6rem',
          fontSize: '0.78rem', color: 'var(--color-muted)',
          background: 'rgba(245,158,11,0.05)', borderRadius: 6, padding: '0.5rem 0.6rem',
        }}>
          ✅ Full access to <strong>{m.business_name}</strong> — sales, purchases, products, financials, expenses. Company-level records are not accessible.
        </div>
      )}

      {!m.deactivated_at && m.role === 'staff' && (
        <PermissionsPanel member={m} onSaved={onSaved} />
      )}
    </div>
  );
}

const ROLE_DEFS = {
  manager: {
    label: 'Manager',
    color: 'rgba(245,158,11,0.15)',
    textColor: '#f59e0b',
    desc: 'Full access to their assigned business: sales, purchases, products, financials, expenses. Cannot access other businesses or company-level records.',
  },
  staff: {
    label: 'Staff',
    color: 'rgba(99,102,241,0.12)',
    textColor: 'var(--color-primary)',
    desc: 'Handles day-to-day sales and stock. Cannot see financial records.',
  },
};

export default function Team() {
  const { user, isEnterprise } = useAuth();
  const { toast }       = useToast();
  const [members, setMembers]       = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: '', dialCode: '+267', localNumber: '', business_id: '', role: 'staff' });
  const [createdPass, setCreatedPass] = useState(null);  // { pass, name, reset }

  const load = () => teamAPI.list().then(r => setMembers(r.data.members || [])).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    load();
    if (isEnterprise) {
      companyAPI.listBiz().then(r => setBusinesses(r.data.businesses || [])).catch(() => {});
    }
  }, [isEnterprise]);

  if (user?.role !== 'owner') return <div className="page"><p className="text-muted">Owner access only.</p></div>;

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const phone = `${form.dialCode}${form.localNumber.replace(/^0/, '')}`;
      const res = await teamAPI.invite({ name: form.name, phone, business_id: form.business_id || undefined, role: form.role });
      setCreatedPass({ pass: res.data.tempPass, name: form.name, reset: false });
      setShowInvite(false);
      setForm({ name: '', dialCode: '+267', localNumber: '', business_id: '', role: 'staff' });
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed', 'error'); }
  };

  const handleDeactivate = async (id, role) => {
    if (!window.confirm(`Deactivate this ${role}? They will be signed out and can no longer log in.`)) return;
    try { await teamAPI.deactivate(id); load(); } catch { toast('Failed', 'error'); }
  };

  const handleReactivate = async (id) => {
    try { await teamAPI.reactivate(id); load(); } catch { toast('Failed', 'error'); }
  };

  // Staff sign in by phone and have no email, so the owner is their recovery
  // path: a new temporary password, which they must replace at next sign-in.
  const handleResetPassword = async (m) => {
    if (!window.confirm(`Reset ${m.name}'s password? They will be signed out and get a new temporary password from you.`)) return;
    try {
      const res = await teamAPI.resetPassword(m.id);
      setCreatedPass({ pass: res.data.tempPass, name: m.name, reset: true });
      load();
    } catch (err) { toast(err.response?.data?.error || 'Failed to reset password', 'error'); }
  };

  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;

  const managers = members.filter(m => m.role === 'manager');
  const staff    = members.filter(m => m.role === 'staff');

  return (
    <div className="page">
      <BackButton />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Team</h1>
        <button className="btn-primary" onClick={() => setShowInvite(!showInvite)}>+ Invite</button>
      </div>

      {createdPass && (
        <div className="card" style={{ marginBottom: '1rem', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)', marginBottom: '0.35rem' }}>
            {createdPass.reset ? `Password reset for ${createdPass.name}.` : `${createdPass.name} added.`}{' '}
            Give them this temporary password in person — they will be asked to choose their own when they sign in:
          </div>
          <div style={{ fontWeight: 700, fontSize: '1.2rem', letterSpacing: '0.1em', color: 'var(--color-success)', marginBottom: '0.5rem' }}>
            {createdPass.pass}
          </div>
          <button onClick={() => setCreatedPass(null)} style={{ fontSize: '0.78rem', background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', padding: 0 }}>
            Dismiss
          </button>
        </div>
      )}

      {showInvite && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Name</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="Full name" />
            </div>

            <div style={{ margin: 0 }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.35rem' }}>Mobile number</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  value={form.dialCode}
                  onChange={e => setForm(f => ({ ...f, dialCode: e.target.value }))}
                  style={{ width: '110px', flexShrink: 0, fontSize: '0.85rem' }}
                >
                  {COUNTRIES.filter(c => c.dial).map(c => (
                    <option key={c.code} value={c.dial}>{c.flag || c.code} {c.dial}</option>
                  ))}
                </select>
                <input
                  type="tel" value={form.localNumber}
                  onChange={e => setForm(f => ({ ...f, localNumber: e.target.value }))}
                  required placeholder="71 234 567" style={{ flex: 1 }}
                />
              </div>
            </div>

            {/* Role selector */}
            <div>
              <label style={{ fontSize: '0.85rem', color: 'var(--color-muted)', display: 'block', marginBottom: '0.4rem' }}>Role</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {['staff', 'manager'].map(r => (
                  <button
                    key={r} type="button"
                    onClick={() => setForm(f => ({ ...f, role: r }))}
                    style={{
                      flex: 1, padding: '0.55rem 0.4rem', borderRadius: 8, textAlign: 'left',
                      border: `1px solid ${form.role === r ? 'var(--color-primary)' : 'var(--color-border)'}`,
                      background: form.role === r ? 'rgba(99,102,241,0.1)' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: ROLE_DEFS[r].textColor }}>
                      {ROLE_DEFS[r].label}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)', marginTop: 2 }}>
                      {ROLE_DEFS[r].desc}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {(businesses.length > 1 || form.role === 'manager') && (
              <div className="form-group" style={{ margin: 0 }}>
                <label>Assign to business {form.role === 'manager' ? '(required for manager)' : ''}</label>
                <select
                  value={form.business_id}
                  onChange={e => setForm(f => ({ ...f, business_id: e.target.value }))}
                  required={form.role === 'manager'}
                >
                  <option value="">— Select business —</option>
                  {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                Add {form.role === 'manager' ? 'Manager' : 'Staff'}
              </button>
              <button type="button" onClick={() => setShowInvite(false)} style={{ flex: 1, background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', color: 'var(--color-muted)' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {managers.length === 0 && staff.length === 0 && !showInvite && (
        <p className="text-muted" style={{ fontSize: '0.9rem' }}>No team members yet. Add someone to get started.</p>
      )}

      {/* Managers section */}
      {managers.length > 0 && (
        <>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem', marginTop: '0.25rem' }}>
            Managers
          </div>
          {managers.map(m => (
            <MemberCard key={m.id} m={m} onDeactivate={handleDeactivate} onReactivate={handleReactivate} onResetPassword={handleResetPassword} onSaved={load} />
          ))}
        </>
      )}

      {/* Staff section */}
      {staff.length > 0 && (
        <>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem', marginTop: managers.length > 0 ? '1rem' : '0.25rem' }}>
            Staff
          </div>
          {staff.map(m => (
            <MemberCard key={m.id} m={m} onDeactivate={handleDeactivate} onReactivate={handleReactivate} onResetPassword={handleResetPassword} onSaved={load} />
          ))}
        </>
      )}
    </div>
  );
}
